import React from 'react';

interface LogoLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  fullScreen?: boolean;
}

/**
 * Reusable loading component with store logo animation.
 * Use everywhere: page loading, data fetching, form submission.
 * 
 * Usage:
 *   <LogoLoader />                              — inline, medium
 *   <LogoLoader size="sm" />                    — small inline
 *   <LogoLoader fullScreen />                   — full page overlay
 *   <LogoLoader text="Loading products..." />   — with message
 */
const LogoLoader: React.FC<LogoLoaderProps> = ({ size = 'md', text, fullScreen = false }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-14 h-14',
    lg: 'w-20 h-20',
  };

  const containerClasses = {
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-12',
  };

  const content = (
    <div className={`flex flex-col items-center justify-center ${!fullScreen ? containerClasses[size] : ''}`}>
      {/* Logo with animation */}
      <div className="relative">
        {/* Rotating ring */}
        <div className={`absolute inset-0 ${sizeClasses[size]} border-2 border-gray-200 border-t-gray-800 rounded-full animate-spin`} 
          style={{ animationDuration: '1s' }} 
        />
        {/* Logo */}
        <img
          src="/logo/logo.PNG"
          alt="Loading"
          className={`${sizeClasses[size]} object-contain rounded-full p-1 animate-pulse`}
          style={{ animationDuration: '1.5s' }}
        />
      </div>

      {/* Loading text */}
      {text && (
        <p className={`mt-3 text-gray-500 ${size === 'sm' ? 'text-xs' : 'text-sm'} animate-pulse`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white/80 backdrop-blur-sm flex items-center justify-center">
        {content}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full">
      {content}
    </div>
  );
};

export default LogoLoader;
